import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Facebook, MessageCircle, Linkedin, Phone, 
  CheckCircle2, XCircle, RefreshCw, ExternalLink, Settings2,
  BarChart3, MessageSquare, Users, Zap, Music2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Facebook Config Component
const FacebookConfigCard = ({ config, onSave }) => {
  const [form, setForm] = useState({
    page_id: config?.settings?.page_id || '',
    page_name: config?.settings?.page_name || '',
    access_token: config?.settings?.access_token || '',
    pixel_id: config?.settings?.pixel_id || '',
    app_id: config?.settings?.app_id || '',
    analytics_enabled: config?.settings?.analytics_enabled || false
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/social-config/facebook/setup`, form);
      toast.success('Facebook configuration saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save Facebook config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/social-config/facebook/test`);
      toast.success('Facebook connection test successful');
    } catch (error) {
      toast.error('Facebook connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center">
              <Facebook className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">Facebook</CardTitle>
              <CardDescription>Page & Analytics Integration</CardDescription>
            </div>
          </div>
          <Badge className={config ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {config ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Page Name</Label>
            <Input value={form.page_name} onChange={(e) => setForm({ ...form, page_name: e.target.value })} placeholder="Your Company Page" />
          </div>
          <div className="space-y-2">
            <Label>Page ID</Label>
            <Input value={form.page_id} onChange={(e) => setForm({ ...form, page_id: e.target.value })} placeholder="123456789" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Access Token</Label>
          <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder="EAAxxxxxxxx..." />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>App ID</Label>
            <Input value={form.app_id} onChange={(e) => setForm({ ...form, app_id: e.target.value })} placeholder="App ID from Meta Developer" />
          </div>
          <div className="space-y-2">
            <Label>Pixel ID (Analytics)</Label>
            <Input value={form.pixel_id} onChange={(e) => setForm({ ...form, pixel_id: e.target.value })} placeholder="Pixel ID for tracking" />
          </div>
        </div>

        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Enable Facebook Analytics</span>
          </div>
          <Switch checked={form.analytics_enabled} onCheckedChange={(v) => setForm({ ...form, analytics_enabled: v })} />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleTest} disabled={testing || !config}>
            <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-facebook-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// WhatsApp Config Component
const WhatsAppConfigCard = ({ config, onSave }) => {
  const [form, setForm] = useState({
    phone_number: config?.settings?.phone_number || '',
    business_account_id: config?.settings?.business_account_id || '',
    api_key: config?.settings?.api_key || '',
    webhook_url: config?.settings?.webhook_url || '',
    three_cx_enabled: config?.settings?.three_cx_enabled || false,
    three_cx_server: config?.settings?.three_cx_server || '',
    three_cx_extension: config?.settings?.three_cx_extension || ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/social-config/whatsapp/setup`, form);
      toast.success('WhatsApp configuration saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save WhatsApp config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/social-config/whatsapp/test`);
      toast.success('WhatsApp connection test successful');
    } catch (error) {
      toast.error('WhatsApp connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">WhatsApp Business</CardTitle>
              <CardDescription>Messaging & 3CX Integration</CardDescription>
            </div>
          </div>
          <Badge className={config ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {config ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Business Phone Number</Label>
            <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+27 XX XXX XXXX" />
          </div>
          <div className="space-y-2">
            <Label>Business Account ID</Label>
            <Input value={form.business_account_id} onChange={(e) => setForm({ ...form, business_account_id: e.target.value })} placeholder="Meta Business ID" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>API Key / Access Token</Label>
          <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="WhatsApp Cloud API token" />
        </div>

        <div className="space-y-2">
          <Label>Webhook URL</Label>
          <Input value={form.webhook_url} onChange={(e) => setForm({ ...form, webhook_url: e.target.value })} placeholder="https://your-domain.com/api/whatsapp/webhook" />
        </div>

        {/* 3CX Integration */}
        <div className="p-4 border rounded-lg space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Phone className="h-4 w-4 text-blue-600" />
              <span className="font-medium">3CX Phone Integration</span>
            </div>
            <Switch checked={form.three_cx_enabled} onCheckedChange={(v) => setForm({ ...form, three_cx_enabled: v })} />
          </div>
          
          {form.three_cx_enabled && (
            <div className="grid grid-cols-2 gap-4 pt-2">
              <div className="space-y-2">
                <Label>3CX Server URL</Label>
                <Input value={form.three_cx_server} onChange={(e) => setForm({ ...form, three_cx_server: e.target.value })} placeholder="https://your-company.3cx.co.za" />
              </div>
              <div className="space-y-2">
                <Label>Extension</Label>
                <Input value={form.three_cx_extension} onChange={(e) => setForm({ ...form, three_cx_extension: e.target.value })} placeholder="101" />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleTest} disabled={testing || !config}>
            <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-whatsapp-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// LinkedIn Config Component
const LinkedInConfigCard = ({ config, onSave }) => {
  const [form, setForm] = useState({
    company_id: config?.settings?.company_id || '',
    company_name: config?.settings?.company_name || '',
    access_token: config?.settings?.access_token || '',
    page_url: config?.settings?.page_url || ''
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/social-config/linkedin/setup`, form);
      toast.success('LinkedIn configuration saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save LinkedIn config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/social-config/linkedin/test`);
      toast.success('LinkedIn connection test successful');
    } catch (error) {
      toast.error('LinkedIn connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-800 flex items-center justify-center">
              <Linkedin className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">LinkedIn</CardTitle>
              <CardDescription>Company Page Integration</CardDescription>
            </div>
          </div>
          <Badge className={config ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {config ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Company Name</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} placeholder="Your Company Name" />
          </div>
          <div className="space-y-2">
            <Label>Company ID</Label>
            <Input value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })} placeholder="LinkedIn Company ID" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Page URL</Label>
          <Input value={form.page_url} onChange={(e) => setForm({ ...form, page_url: e.target.value })} placeholder="https://www.linkedin.com/company/your-company" />
        </div>

        <div className="space-y-2">
          <Label>Access Token</Label>
          <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder="LinkedIn API token" />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleTest} disabled={testing || !config}>
            <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-linkedin-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// TikTok Config Component
const TikTokConfigCard = ({ config, onSave }) => {
  const [form, setForm] = useState({
    business_account_id: config?.settings?.business_account_id || '',
    username: config?.settings?.username || '',
    access_token: config?.settings?.access_token || '',
    pixel_id: config?.settings?.pixel_id || '',
    profile_url: config?.settings?.profile_url || '',
    analytics_enabled: config?.settings?.analytics_enabled || false,
    ads_enabled: config?.settings?.ads_enabled || false
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/social-config/tiktok/setup`, form);
      toast.success('TikTok configuration saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save TikTok config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/social-config/tiktok/test`);
      toast.success('TikTok connection test successful');
    } catch (error) {
      toast.error('TikTok connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-black flex items-center justify-center">
              <Music2 className="h-5 w-5 text-[#00f2ea]" />
            </div>
            <div>
              <CardTitle className="text-lg">TikTok</CardTitle>
              <CardDescription>Business Account & Ads Integration</CardDescription>
            </div>
          </div>
          <Badge className={config ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {config ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Username</Label>
            <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="@yourcompany" />
          </div>
          <div className="space-y-2">
            <Label>Business Account ID</Label>
            <Input value={form.business_account_id} onChange={(e) => setForm({ ...form, business_account_id: e.target.value })} placeholder="TikTok Business ID" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Profile URL</Label>
          <Input value={form.profile_url} onChange={(e) => setForm({ ...form, profile_url: e.target.value })} placeholder="https://www.tiktok.com/@yourcompany" />
        </div>

        <div className="space-y-2">
          <Label>Access Token</Label>
          <Input type="password" value={form.access_token} onChange={(e) => setForm({ ...form, access_token: e.target.value })} placeholder="TikTok API Access Token" />
        </div>

        <div className="space-y-2">
          <Label>Pixel ID (TikTok Analytics)</Label>
          <Input value={form.pixel_id} onChange={(e) => setForm({ ...form, pixel_id: e.target.value })} placeholder="TikTok Pixel ID for tracking" />
        </div>

        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Enable TikTok Analytics</span>
          </div>
          <Switch checked={form.analytics_enabled} onCheckedChange={(v) => setForm({ ...form, analytics_enabled: v })} />
        </div>

        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">Enable TikTok Ads</span>
          </div>
          <Switch checked={form.ads_enabled} onCheckedChange={(v) => setForm({ ...form, ads_enabled: v })} />
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleTest} disabled={testing || !config}>
            <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-tiktok-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// 3CX Config Component
const ThreeCXConfigCard = ({ config, onSave }) => {
  const [form, setForm] = useState({
    server_url: config?.settings?.server_url || '',
    extension: config?.settings?.extension || '',
    api_key: config?.settings?.api_key || '',
    queue_name: config?.settings?.queue_name || '',
    auto_answer: config?.settings?.auto_answer || false
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/social-config/3cx/setup`, form);
      toast.success('3CX configuration saved');
      onSave();
    } catch (error) {
      toast.error('Failed to save 3CX config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      await axios.post(`${API}/social-config/3cx/test`);
      toast.success('3CX connection test successful');
    } catch (error) {
      toast.error('3CX connection test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-orange-600 flex items-center justify-center">
              <Phone className="h-5 w-5 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg">3CX Phone System</CardTitle>
              <CardDescription>VoIP & Call Center Integration</CardDescription>
            </div>
          </div>
          <Badge className={config ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'}>
            {config ? 'Configured' : 'Not Configured'}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>3CX Server URL</Label>
            <Input value={form.server_url} onChange={(e) => setForm({ ...form, server_url: e.target.value })} placeholder="https://company.3cx.co.za:5001" />
          </div>
          <div className="space-y-2">
            <Label>Extension Number</Label>
            <Input value={form.extension} onChange={(e) => setForm({ ...form, extension: e.target.value })} placeholder="101" />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>API Key</Label>
          <Input type="password" value={form.api_key} onChange={(e) => setForm({ ...form, api_key: e.target.value })} placeholder="3CX API Key" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Queue Name</Label>
            <Input value={form.queue_name} onChange={(e) => setForm({ ...form, queue_name: e.target.value })} placeholder="Sales Queue" />
          </div>
          <div className="space-y-2">
            <Label>Auto Answer</Label>
            <div className="flex items-center h-10">
              <Switch checked={form.auto_answer} onCheckedChange={(v) => setForm({ ...form, auto_answer: v })} />
              <span className="ml-2 text-sm text-muted-foreground">{form.auto_answer ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={handleTest} disabled={testing || !config}>
            <RefreshCw className={`h-4 w-4 mr-2 ${testing ? 'animate-spin' : ''}`} />
            Test Connection
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-3cx-btn">
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Social Config Page
export const SocialConfigPage = () => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadConfigs();
  }, []);

  const loadConfigs = async () => {
    try {
      const res = await axios.get(`${API}/social-config`);
      setConfigs(res.data);
    } catch (error) {
      console.error('Failed to load configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConfig = (platform) => configs.find(c => c.platform === platform);

  const configuredCount = ['facebook', 'whatsapp', 'linkedin', 'tiktok', '3cx'].filter(p => getConfig(p)).length;

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="social-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Social & Messaging</h1>
          <p className="text-muted-foreground mt-1">Configure social media and communication integrations</p>
        </div>
        <Badge variant="outline" className="text-lg px-4 py-2">
          {configuredCount}/5 Configured
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className={`border-2 ${getConfig('facebook') ? 'border-blue-200 bg-blue-50/50' : 'border-dashed'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Facebook className={`h-8 w-8 ${getConfig('facebook') ? 'text-blue-600' : 'text-slate-300'}`} />
            <div>
              <p className="font-medium">Facebook</p>
              <p className="text-xs text-muted-foreground">{getConfig('facebook') ? 'Connected' : 'Not connected'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-2 ${getConfig('whatsapp') ? 'border-emerald-200 bg-emerald-50/50' : 'border-dashed'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <MessageCircle className={`h-8 w-8 ${getConfig('whatsapp') ? 'text-emerald-600' : 'text-slate-300'}`} />
            <div>
              <p className="font-medium">WhatsApp</p>
              <p className="text-xs text-muted-foreground">{getConfig('whatsapp') ? 'Connected' : 'Not connected'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-2 ${getConfig('linkedin') ? 'border-blue-200 bg-blue-50/50' : 'border-dashed'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Linkedin className={`h-8 w-8 ${getConfig('linkedin') ? 'text-blue-800' : 'text-slate-300'}`} />
            <div>
              <p className="font-medium">LinkedIn</p>
              <p className="text-xs text-muted-foreground">{getConfig('linkedin') ? 'Connected' : 'Not connected'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-2 ${getConfig('tiktok') ? 'border-pink-200 bg-pink-50/50' : 'border-dashed'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Music2 className={`h-8 w-8 ${getConfig('tiktok') ? 'text-black' : 'text-slate-300'}`} />
            <div>
              <p className="font-medium">TikTok</p>
              <p className="text-xs text-muted-foreground">{getConfig('tiktok') ? 'Connected' : 'Not connected'}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-2 ${getConfig('3cx') ? 'border-orange-200 bg-orange-50/50' : 'border-dashed'}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <Phone className={`h-8 w-8 ${getConfig('3cx') ? 'text-orange-600' : 'text-slate-300'}`} />
            <div>
              <p className="font-medium">3CX</p>
              <p className="text-xs text-muted-foreground">{getConfig('3cx') ? 'Connected' : 'Not connected'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Configuration Tabs */}
      <Tabs defaultValue="facebook" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="facebook" className="flex items-center gap-2">
            <Facebook className="h-4 w-4" /> Facebook
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4" /> WhatsApp
          </TabsTrigger>
          <TabsTrigger value="linkedin" className="flex items-center gap-2">
            <Linkedin className="h-4 w-4" /> LinkedIn
          </TabsTrigger>
          <TabsTrigger value="tiktok" className="flex items-center gap-2">
            <Music2 className="h-4 w-4" /> TikTok
          </TabsTrigger>
          <TabsTrigger value="3cx" className="flex items-center gap-2">
            <Phone className="h-4 w-4" /> 3CX
          </TabsTrigger>
        </TabsList>

        <TabsContent value="facebook">
          <FacebookConfigCard config={getConfig('facebook')} onSave={loadConfigs} />
        </TabsContent>

        <TabsContent value="whatsapp">
          <WhatsAppConfigCard config={getConfig('whatsapp')} onSave={loadConfigs} />
        </TabsContent>

        <TabsContent value="linkedin">
          <LinkedInConfigCard config={getConfig('linkedin')} onSave={loadConfigs} />
        </TabsContent>

        <TabsContent value="tiktok">
          <TikTokConfigCard config={getConfig('tiktok')} onSave={loadConfigs} />
        </TabsContent>

        <TabsContent value="3cx">
          <ThreeCXConfigCard config={getConfig('3cx')} onSave={loadConfigs} />
        </TabsContent>
      </Tabs>

      {/* Help Section */}
      <Card className="bg-slate-50">
        <CardContent className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <Settings2 className="h-5 w-5" /> Integration Help
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="font-medium text-slate-700">Facebook Setup:</p>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                <li>Create a Meta Developer account</li>
                <li>Create an App and get Page Access Token</li>
                <li>Configure Facebook Pixel for analytics</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-slate-700">WhatsApp Business:</p>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                <li>Set up WhatsApp Business API</li>
                <li>Verify your business phone number</li>
                <li>Configure webhook for incoming messages</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-slate-700">LinkedIn Company:</p>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                <li>Create LinkedIn Company Page</li>
                <li>Generate API credentials from LinkedIn Developer</li>
                <li>Request appropriate permissions</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-slate-700">TikTok Business:</p>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                <li>Create TikTok Business Account</li>
                <li>Register on TikTok for Developers</li>
                <li>Set up TikTok Pixel for analytics</li>
                <li>Configure TikTok Ads Manager</li>
              </ol>
            </div>
            <div>
              <p className="font-medium text-slate-700">3CX Integration:</p>
              <ol className="list-decimal ml-4 text-muted-foreground space-y-1">
                <li>Get your 3CX server URL</li>
                <li>Generate API key from 3CX admin</li>
                <li>Configure extension and queue settings</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
